import { useEffect, useRef, useState, useCallback } from 'react';
import { useGlobal } from '../global/user';
import { resolveLocator } from '../utils/stepLocator';

const TUTORIAL_FIELD_ID = 44;

const ensureTutorialStyles = () => {
  if (document.getElementById('tutorial-click-style')) return;

  const style = document.createElement('style');
  style.id = 'tutorial-click-style';
  style.innerHTML = `
    @keyframes tutorial-click-ripple {
      0% { transform: scale(0.2); opacity: 0.8; }
      50% { transform: scale(1.2); opacity: 0.5; }
      100% { transform: scale(1.5); opacity: 0; }
    }
    @keyframes tutorial-rpg-in {
      0% { opacity: 0; }
      100% { opacity: 1; }
    }

    #tutorial-highlight-ring {
      position: fixed !important;
      display: none;
      box-sizing: border-box;
      z-index: 99998 !important;
      border: 3px solid #1890ff !important;
      border-radius: 6px !important;
      box-shadow: 0 0 0 4px rgba(24, 144, 255, 0.25) !important;
      pointer-events: none !important;
    }
    .tutorial-overlay {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      z-index: 99997 !important;
      background: transparent !important;
      pointer-events: auto !important;
    }

    .tutorial-back-float-btn {
      position: fixed !important;
      top: 20px !important;
      left: 20px !important;
      z-index: 99999 !important;
      padding: 6px 14px;
      cursor: pointer;
      border: 1px solid #d9d9d9;
      background: #1890ff !important;
      color: #ffffff !important;
      border-radius: 6px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-weight: 500;
    }

    /* ---- 對話框 ---- */
    .tutorial-rpg-dialog {
      position: fixed !important;
      left: 50% !important;
      bottom: 28px !important;
      top: auto !important;
      transform: translateX(-50%);
      width: min(720px, calc(100vw - 40px));
      max-height: 90vh;
      min-height: 110px;
      box-sizing: border-box;
      z-index: 99999 !important;
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 22px 20px 14px;
      background: #ffffff;
      border: 1px solid #d9d9d9;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      color: #333;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      user-select: none;
      animation: tutorial-rpg-in 0.2s ease-out;
    }
    .tutorial-rpg-dialog.tutorial-rpg-dialog--top {
      top: 64px !important;
      bottom: auto !important;
    }

    .tutorial-rpg-dialog.tutorial-rpg-dialog--center {
      top: 50% !important;
      bottom: auto !important;
      transform: translate(-50%, -50%);
    }
    .tutorial-rpg-name {
      position: absolute;
      top: -12px;
      left: 16px;
      padding: 2px 10px;
      background: #1890ff;
      color: #fff;
      font-size: 12px;
      font-weight: 500;
      border-radius: 4px;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }
    .tutorial-rpg-text {
      min-height: 44px;
      font-size: 15px;
      line-height: 1.7;
      color: #333;
      word-break: break-word;
    }
    .tutorial-rpg-image {
      display: none;
      align-self: center;
      max-width: 100%;
      max-height: 50vh;
      object-fit: contain;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
    }
    .tutorial-rpg-actions {
      display: flex;
      justify-content: flex-end;
    }
    .tutorial-rpg-next-btn {
      padding: 4px 12px;
      cursor: pointer;
      background: #1890ff !important;
      color: #fff !important;
      border: none;
      border-radius: 4px;
      font-size: 13px;
    }
  `;
  document.head.appendChild(style);
};

const isGuideUi = (el) =>
  el.closest?.('#tutorial-step-panel, #tutorial-back-float-btn, #tutorial-overlay, #tutorial-highlight-ring');

const getHighlightRing = () => {
  let ring = document.getElementById('tutorial-highlight-ring');
  if (!ring) {
    ring = document.createElement('div');
    ring.id = 'tutorial-highlight-ring';
    document.body.appendChild(ring);
  }
  return ring;
};

const findElement = (selectorType, valueStr, matchIndex = 0) => {
  if (selectorType === 'TEXT' || selectorType === 'CLICK_TEXT') {
    const candidates = Array.from(
      document.querySelectorAll('button, [role="tab"], a, .ant-tabs-tab, .MuiTab-root, .ant-btn, span, div')
    ).filter((el) => !isGuideUi(el));

    const matchedCandidates = candidates.filter(
      (el) => (el.textContent?.trim() === valueStr && el.children.length === 0) || el.textContent?.trim() === valueStr
    );

    return matchedCandidates[matchIndex] || matchedCandidates[0] || null;
  }
  if (selectorType === 'CSS') {
    const candidates = Array.from(document.querySelectorAll(valueStr));
    return candidates[matchIndex] || candidates[0] || null;
  }
  return null;
};

const findStepElement = (step) => {
  if (!step) return null;
  
  if (step.selector_type === 'LOCATOR') {
    if (step.locator) {
      try {
        const candidates = Array.from(document.querySelectorAll(step.locator));
        if (candidates.length > 0) {
          return candidates[step.match_index ?? 0] || candidates[0];
        }
      } catch (e) {
      }
    }
    return resolveLocator(document, step.locator, isGuideUi);
  }

  const targetVal = step.target_value || step.text;
  if (!targetVal) return null;
  
  return findElement(step.selector_type || 'TEXT', String(targetVal).trim(), step.match_index ?? 0);
};

const hasTarget = (step) =>
  !!step &&
  step.type !== 'IMAGE' &&
  (step.selector_type === 'LOCATOR' ? !!step.locator : (!!step.target_value || !!step.text));

const waitForStepElement = async (step, timeout = 3000) => {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const element = findStepElement(step);
    if (element) return element;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return null;
};

const triggerVisualClick = async (element) => {
  if (!element) return;

  ensureTutorialStyles();

  const originalPosition = element.style.position;
  if (getComputedStyle(element).position === 'static') {
    element.style.position = 'relative';
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const ripple = document.createElement('span');
  const rect = element.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height);

  ripple.style.width = ripple.style.height = `${size}px`;
  ripple.style.left = `${rect.width / 2 - size / 2}px`;
  ripple.style.top = `${rect.height / 2 - size / 2}px`;
  ripple.style.position = 'absolute';
  ripple.style.borderRadius = '50%';
  ripple.style.backgroundColor = 'rgba(24, 144, 255, 0.4)';
  ripple.style.transform = 'scale(0)';
  ripple.style.animation = 'tutorial-click-ripple 0.6s ease-out';
  ripple.style.pointerEvents = 'none';

  element.appendChild(ripple);

  await new Promise((resolve) => setTimeout(resolve, 400));

  element.click();

  setTimeout(() => {
    ripple.remove();
    element.style.position = originalPosition;
  }, 300);
};

const getDisplayText = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  return String(item.text ?? item.label ?? item.target_value ?? '');
};

export const useFeatureGuide = ({
  location,
  setActiveTab,
  setRestoredFieldData,
  stepsFromDB = [],
}) => {
  const { field, fields, setField } = useGlobal();
  const [needTutorialField, setNeedTutorialField] = useState(false);
  const fieldAttemptsRef = useRef(0);
  const hasInitializedRef = useRef(false);
  const isRunningRef = useRef(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [activeSteps, setActiveSteps] = useState([]);
  const [displaySteps, setDisplaySteps] = useState([]);
  const [isGuideActive, setIsGuideActive] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const restoreDataRef = useRef(null);

  useEffect(() => {
    const restoreData = location.state?.restoreData;
    if (!restoreData) return;
    restoreDataRef.current = restoreData;

    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    if (setRestoredFieldData) {
      setRestoredFieldData(restoreData);
    }

    const targetPathname = String(restoreData.targetPath || '').split('?')[0];
    if (targetPathname === '/control') {
      setNeedTutorialField(true);
    }

    let rawSteps = stepsFromDB.length > 0 ? stepsFromDB : restoreData.pathSteps;
    if (typeof rawSteps === 'string') {
      try {
        rawSteps = JSON.parse(rawSteps);
      } catch (e) {
        console.error('[FeatureGuide] 解析 pathSteps 失敗', e);
        rawSteps = [];
      }
    }
    if (!Array.isArray(rawSteps)) rawSteps = [];

    let rawDisplaySteps = restoreData.displayPathSteps || rawSteps;
    if (typeof rawDisplaySteps === 'string') {
      try {
        rawDisplaySteps = JSON.parse(rawDisplaySteps);
      } catch (e) {
        rawDisplaySteps = rawSteps;
      }
    }
    if (!Array.isArray(rawDisplaySteps)) rawDisplaySteps = rawSteps;

    // 格式化步驟並接收 match_index
    const formattedSteps = rawSteps.map((step, index) => {
      if (typeof step === 'string') {
        return {
          step_order: index + 1,
          type: 'CLICK_BUTTON',
          target_value: step,
          selector_type: 'TEXT',
          locator: null,
          match_index: 0,
          image: '',
        };
      }
      const isImage = step.type === 'IMAGE';
      return {
        step_order: step.step_order ?? index + 1,
        type: step.type || 'CLICK_BUTTON',
        target_value: isImage ? '' : step.target_value || step.text || '',
        selector_type: step.selector_type || 'TEXT',
        locator: step.locator || null,
        match_index: step.match_index ?? 0, // 讀取錄製端帶入的 match_index
        image: isImage ? step.image || '' : '',
      };
    });

    if (formattedSteps.length > 0) {
      setActiveSteps(formattedSteps);
      setDisplaySteps(rawDisplaySteps);
      setCurrentStepIndex(0);
      setIsGuideActive(true);
      setIsFinished(false);
    } else {
      setIsGuideActive(true);
      setIsFinished(true);
    }
  }, [location.state?.restoreData, stepsFromDB, setRestoredFieldData]);


  useEffect(() => {
    if (!needTutorialField || isFinished) return;
    if (typeof setField !== 'function' || !Array.isArray(fields) || fields.length === 0) return;

    const target = fields.find((f) => String(f.field_id) === String(TUTORIAL_FIELD_ID));
    if (!target) {
      console.warn(`[FeatureGuide] 帳號的場域清單中沒有教學場域 ID ${TUTORIAL_FIELD_ID}`);
      return;
    }

    if (field === target.field_id) {
      fieldAttemptsRef.current = 0;
      return;
    }

    if (fieldAttemptsRef.current >= 5) return;
    fieldAttemptsRef.current += 1;
    setField(target.field_id);
  }, [needTutorialField, isFinished, fields, field, setField]);

  const handleNextStep = useCallback(async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    try {
      const step = activeSteps[currentStepIndex];
      if (hasTarget(step)) {
        const targetEl = await waitForStepElement(step, 3000);
        if (targetEl) {
          await triggerVisualClick(targetEl);
        }
      }

      if (currentStepIndex + 1 < activeSteps.length) {
        setCurrentStepIndex((prev) => prev + 1);
      } else {
        setIsFinished(true);
      }
    } finally {
      isRunningRef.current = false;
    }
  }, [currentStepIndex, activeSteps]);

  const handleBack = useCallback(() => {
    window.history.back();
  }, []);

  useEffect(() => {
    ensureTutorialStyles();

    let overlay = document.getElementById('tutorial-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'tutorial-overlay';
      overlay.className = 'tutorial-overlay';
      overlay.style.display = 'none';
      document.body.appendChild(overlay);
    }

    let backBtn = document.getElementById('tutorial-back-float-btn');
    if (!backBtn) {
      backBtn = document.createElement('button');
      backBtn.id = 'tutorial-back-float-btn';
      backBtn.className = 'tutorial-back-float-btn';
      backBtn.innerText = '返回';
      backBtn.style.display = 'none';
      document.body.appendChild(backBtn);
    }
    backBtn.onclick = handleBack;

    return () => {
      document.getElementById('tutorial-overlay')?.remove();
      document.getElementById('tutorial-back-float-btn')?.remove();
      document.getElementById('tutorial-step-panel')?.remove();
      document.getElementById('tutorial-highlight-ring')?.remove();
    };
  }, [handleBack]);

  useEffect(() => {
    ensureTutorialStyles();

    let panel = document.getElementById('tutorial-step-panel');
    if (!panel) {
      panel = document.createElement('div');
      panel.id = 'tutorial-step-panel';
      panel.className = 'tutorial-rpg-dialog';

      const nameEl = document.createElement('div');
      nameEl.className = 'tutorial-rpg-name';

      const textEl = document.createElement('div');
      textEl.className = 'tutorial-rpg-text';

      const imgEl = document.createElement('img');
      imgEl.className = 'tutorial-rpg-image';

      const actions = document.createElement('div');
      actions.className = 'tutorial-rpg-actions';

      const nextBtn = document.createElement('button');
      nextBtn.id = 'tutorial-next-btn';
      nextBtn.className = 'tutorial-rpg-next-btn';
      actions.appendChild(nextBtn);

      panel.appendChild(nameEl);
      panel.appendChild(textEl);
      panel.appendChild(imgEl);
      panel.appendChild(actions);
      document.body.appendChild(panel);
    }

    const nameEl = panel.querySelector('.tutorial-rpg-name');
    const textEl = panel.querySelector('.tutorial-rpg-text');
    const imgEl = panel.querySelector('.tutorial-rpg-image');
    const nextBtn = panel.querySelector('.tutorial-rpg-next-btn');

    if (isFinished || activeSteps.length === 0) {
      panel.style.display = 'none';
      return;
    }

    panel.style.display = 'flex';
    const activeOverlay = document.getElementById('tutorial-overlay');
    if (activeOverlay) activeOverlay.style.display = 'block';

    const backBtn = document.getElementById('tutorial-back-float-btn');
    if (backBtn) backBtn.style.display = 'block';

    const step = activeSteps[currentStepIndex];
    const displayItem = displaySteps[currentStepIndex];
    const isImageStep = step?.type === 'IMAGE';
    const isLast = currentStepIndex + 1 >= activeSteps.length;
    const fullText = getDisplayText(displayItem) || step?.target_value || '';

    panel.classList.remove('tutorial-rpg-dialog--top', 'tutorial-rpg-dialog--center');

    const imageSrc = isImageStep
      ? step.image || (displayItem && typeof displayItem === 'object' ? displayItem.image : '') || ''
      : '';
    if (imageSrc) {
      imgEl.onerror = () => {
        imgEl.style.display = 'none';
      };
      imgEl.src = imageSrc;
      imgEl.alt = fullText;
      imgEl.style.display = 'block';
    } else {
      imgEl.onerror = null;
      imgEl.removeAttribute('src');
      imgEl.style.display = 'none';
    }

    if (isImageStep) {
      panel.classList.add('tutorial-rpg-dialog--center');
    }

    nameEl.textContent = `導覽 ${currentStepIndex + 1} / ${activeSteps.length}`;
    nextBtn.textContent = isLast ? '完成' : '下一步';
    nextBtn.onclick = handleNextStep;

    textEl.textContent = fullText;
    textEl.style.display = fullText ? '' : 'none';
  }, [isGuideActive, currentStepIndex, activeSteps, displaySteps, isFinished, handleNextStep]);

  useEffect(() => {
    if (isFinished || activeSteps.length === 0) return undefined;
    const step = activeSteps[currentStepIndex];
    if (!hasTarget(step)) return undefined;

    ensureTutorialStyles();
    const ring = getHighlightRing();
    const panel = document.getElementById('tutorial-step-panel');

    let rafId = 0;
    let el = null;
    let scrolledEl = null;
    let lastSearch = 0;

    const tick = (now) => {
      if (!el || !el.isConnected) {
        el = null;
        if (now - lastSearch >= 100) {
          lastSearch = now;
          el = findStepElement(step);
        }
      }

      let rect = null;
      if (el) {
        if (scrolledEl !== el) {
          scrolledEl = el;
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        rect = el.getBoundingClientRect();
      }

      if (rect && rect.width > 0 && rect.height > 0) {
        ring.style.display = 'block';
        ring.style.top = `${rect.top - 4}px`;
        ring.style.left = `${rect.left - 4}px`;
        ring.style.width = `${rect.width + 8}px`;
        ring.style.height = `${rect.height + 8}px`;

        if (panel && !panel.classList.contains('tutorial-rpg-dialog--center')) {
          panel.classList.toggle(
            'tutorial-rpg-dialog--top',
            rect.top + rect.height / 2 > window.innerHeight / 2
          );
        }
      } else {
        ring.style.display = 'none';
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      ring.style.display = 'none';
    };
  }, [isGuideActive, currentStepIndex, activeSteps, isFinished]);
};